import { ESdChangeReason, IKuikaStyle, InvisibleStylePart, ITransformItem, Visibility } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { styleSectionNeedsRender } from "./styling-window-helpers";
import TransformAccordion from "./transform-accordion/transform-accordion";
import TransformSettingsPopoverContent, {
  TransformSettingsValue
} from "./transform-settings-popover/transform-settings-popover";

export interface IEffectsTransformsComponentProps {
  form: FormInstance<IKuikaStyle>;
}

interface IEffectsTransformsComponentState {
  placeholder: never[];
}

class EffectsTransformsComponent extends PureSdComponent<
  IEffectsTransformsComponentProps & IWithSDProps,
  IEffectsTransformsComponentState
> {
  constructor(props: IEffectsTransformsComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  private submitForm = (fieldsValue: IKuikaStyle): void => {
    // Do NOT call form.resetFields() here — it unmounts every Form.Item
    // subtree, which resets the local `useState` open-state inside the
    // settings popover (TransformAccordion) and any other popover content
    // rendered under shouldUpdate. Popovers close mid-edit as a result.
    // setFieldsValue alone replaces the transforms array and updates the
    // scalar settings fields; the explicit submit() triggers persistence.
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  private handleTransformsChange = (next: ITransformItem[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.transforms = next;
    }
    this.submitForm(fieldsValue);
  };

  private getCurrentSettings = (): TransformSettingsValue => {
    const effects: IKuikaStyle["effects"] = this.props.form.getFieldValue(["effects"]);
    return {
      transformOriginX: effects?.transformOriginX,
      transformOriginY: effects?.transformOriginY,
      backfaceVisibility: effects?.backfaceVisibility,
      transformStyle: effects?.transformStyle,
      perspective: effects?.perspective,
      childrenPerspective: effects?.childrenPerspective,
      childrenPerspectiveOriginX: effects?.childrenPerspectiveOriginX,
      childrenPerspectiveOriginY: effects?.childrenPerspectiveOriginY
    };
  };

  private handleSettingsPatch = (patch: Partial<TransformSettingsValue>): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      const effects: IKuikaStyle["effects"] = fieldsValue.effects;
      if (!effects) return;
      if (patch.transformOriginX !== undefined) effects.transformOriginX = patch.transformOriginX;
      if (patch.transformOriginY !== undefined) effects.transformOriginY = patch.transformOriginY;
      if (patch.backfaceVisibility !== undefined) effects.backfaceVisibility = patch.backfaceVisibility;
      if (patch.transformStyle !== undefined) effects.transformStyle = patch.transformStyle;
      if (patch.perspective !== undefined) effects.perspective = patch.perspective;
      if (patch.childrenPerspective !== undefined) effects.childrenPerspective = patch.childrenPerspective;
      if (patch.childrenPerspectiveOriginX !== undefined)
        effects.childrenPerspectiveOriginX = patch.childrenPerspectiveOriginX;
      if (patch.childrenPerspectiveOriginY !== undefined)
        effects.childrenPerspectiveOriginY = patch.childrenPerspectiveOriginY;
    }
    this.submitForm(fieldsValue);
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <div className="effects-section__transforms">
        <Form.Item noStyle shouldUpdate>
          {() => {
            const transforms: ITransformItem[] =
              (this.props.form.getFieldValue(["effects", "transforms"]) as ITransformItem[]) || [];
            // Popover content rendered eagerly so the values stay in sync with
            // the live form. Settings popover open-state lives inside the
            // accordion so the gear button is the only trigger — row / ＋ /
            // trash clicks no longer bubble into a wrapping Popover.Trigger.
            const settingsContent: React.ReactNode = (
              <TransformSettingsPopoverContent
                value={this.getCurrentSettings()}
                onChange={this.handleSettingsPatch}
                dataTestId="effects-transform-settings"
              />
            );
            return (
              <TransformAccordion
                items={transforms}
                onChange={this.handleTransformsChange}
                settingsPopoverContent={settingsContent}
                settingsPopoverTitle="Transform Settings"
                dataTestId="effects-transforms"
              />
            );
          }}
        </Form.Item>
      </div>
    );
  }
}

export default withSD<IEffectsTransformsComponentProps>(EffectsTransformsComponent);
